import { useEffect, useState } from 'react';
import { Box, ButtonBase, Dialog, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { appScreenshots, type Application } from '../lib/api';
import { AppMonogram } from './Page';
import { tokens } from '../theme';

/** App identity artwork for catalog cards; screenshots remain on the detail page. */
export function AppCover({ app, variant = 'card' }: { app: Application; variant?: 'card' | 'tile' }) {
  const tile = variant === 'tile';
  return (
    <Box
      sx={{
        aspectRatio: tile ? '16 / 10' : '16 / 9',
        overflow: 'hidden',
        bgcolor: '#fff',
        ...(tile ? { borderRadius: 2, border: `1px solid ${tokens.border}` } : { borderBottom: `1px solid ${tokens.divider}` }),
      }}
    >
      <AppMonogram name={app.name} slug={app.slug} logoUrl={app.logo_url} size={tile ? 'md' : 'hero'} />
    </Box>
  );
}

/** Main screenshot with thumbnails; click to view full size. */
export function ScreenshotGallery({ app }: { app: Application }) {
  const shots = appScreenshots(app);
  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => setIndex(0), [app.id]);

  useEffect(() => {
    if (!open || shots.length < 2) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') setIndex((i) => (i + 1) % shots.length);
      if (e.key === 'ArrowLeft') setIndex((i) => (i - 1 + shots.length) % shots.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, shots.length]);

  if (!shots.length) return null;
  const current = shots[Math.min(index, shots.length - 1)];
  const label = (i: number) => `${app.name} screenshot ${i + 1} of ${shots.length}`;

  return (
    <Box>
      <Typography variant="h2" fontSize={24} mb={2}>
        Screenshots
      </Typography>
      <ButtonBase
        onClick={() => setOpen(true)}
        aria-label={`View ${label(index)} full size`}
        sx={{
          display: 'block',
          width: '100%',
          borderRadius: 2,
          overflow: 'hidden',
          border: `1px solid ${tokens.border}`,
          bgcolor: tokens.strip,
          cursor: 'zoom-in',
          '&:focus-visible': { outline: `3px solid ${tokens.focus}`, outlineOffset: 2 },
        }}
      >
        <Box component="img" src={current} alt={label(index)} sx={{ width: '100%', height: 'auto', display: 'block' }} />
      </ButtonBase>

      {shots.length > 1 && (
        <Stack direction="row" gap={1.5} mt={1.5} flexWrap="wrap">
          {shots.map((src, i) => (
            <ButtonBase
              key={src + i}
              onClick={() => setIndex(i)}
              aria-label={`Show ${label(i)}`}
              aria-pressed={i === index}
              sx={{
                width: 120,
                aspectRatio: '16 / 10',
                borderRadius: 1.5,
                overflow: 'hidden',
                border: `2px solid ${i === index ? tokens.blue : tokens.border}`,
                opacity: i === index ? 1 : 0.75,
                '&:hover': { opacity: 1 },
                '&:focus-visible': { outline: `3px solid ${tokens.focus}`, outlineOffset: 2 },
              }}
            >
              <Box component="img" src={src} alt="" loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top left' }} />
            </ButtonBase>
          ))}
        </Stack>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth={false}
        aria-label={label(index)}
        slotProps={{ paper: { sx: { bgcolor: 'transparent', boxShadow: 'none', m: { xs: 1, sm: 3 }, overflow: 'visible' } } }}
      >
        <Box sx={{ position: 'relative' }}>
          <Box
            component="img"
            src={current}
            alt={label(index)}
            sx={{ display: 'block', maxWidth: 'calc(100vw - 48px)', maxHeight: 'calc(100vh - 96px)', borderRadius: 2, bgcolor: '#fff' }}
          />
          <IconButton
            onClick={() => setOpen(false)}
            aria-label="Close"
            sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(16,24,40,.7)', color: '#fff', '&:hover': { bgcolor: 'rgba(16,24,40,.85)' } }}
          >
            <CloseIcon />
          </IconButton>
          {shots.length > 1 && (
            <>
              <IconButton
                onClick={() => setIndex((i) => (i - 1 + shots.length) % shots.length)}
                aria-label="Previous screenshot"
                sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(16,24,40,.7)', color: '#fff', '&:hover': { bgcolor: 'rgba(16,24,40,.85)' } }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                onClick={() => setIndex((i) => (i + 1) % shots.length)}
                aria-label="Next screenshot"
                sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(16,24,40,.7)', color: '#fff', '&:hover': { bgcolor: 'rgba(16,24,40,.85)' } }}
              >
                <ChevronRightIcon />
              </IconButton>
            </>
          )}
        </Box>
      </Dialog>
    </Box>
  );
}
